import React, { useCallback, useRef, useState } from "react";
import { animated, useSpring } from "react-spring";
import Tippy from "@tippyjs/react";
import { DA_Button } from "../../atoms/Button/Button.component";
import { DA_IconNames } from "../../atoms/Icon/Icon.component";
import S from "./Dropdown.module.scss";

export interface DA_DropdownProps {
    title: string;
    items?: { title: string; onClick: () => void }[];
    icon?: DA_IconNames;
    disabled?: boolean;
}

export const DA_Dropdown = ({ title, items, icon, disabled }: DA_DropdownProps) => {
    const [visible, setVisible] = useState(false);
    const initialAnimationSettings = useRef({
        opacity: 0,
        config: { tension: 300, friction: 15 },
    }).current;
    const [popUpStyle, setSpring] = useSpring(() => initialAnimationSettings);

    const show = useCallback(() => setVisible(true), []);
    const hide = useCallback(() => setVisible(false), []);

    const onMount = useCallback(() => {
        setSpring.start({
            ...initialAnimationSettings,
            opacity: 1,
        });
    }, [setSpring, initialAnimationSettings]);

    const onHide = useCallback(
        ({ unmount }) => {
            setSpring.start({ ...initialAnimationSettings, onRest: unmount });
        },
        [setSpring, initialAnimationSettings]
    );

    return (
        <Tippy
            arrow={false}
            visible={visible}
            animation
            interactive
            placement="bottom-end"
            onClickOutside={hide}
            onMount={onMount}
            onHide={onHide}
            render={(attr) => (
                <animated.div className={S.popUp} style={popUpStyle} {...attr}>
                    {items.map((item, itemIndex) => (
                        <button
                            key={itemIndex}
                            type="button"
                            className={S.buttonItem}
                            onClick={() => {
                                hide();
                                item.onClick();
                            }}
                        >
                            {item.title}
                        </button>
                    ))}
                </animated.div>
            )}
        >
            <DA_Button
                disabled={disabled}
                onClick={visible ? hide : show}
                title={title}
                icon={icon || DA_IconNames.TriangleSmallDown}
                iconRight
            />
        </Tippy>
    );
};
